import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { v4 as uuid } from 'uuid';

import {
  DprStatusTrackerGridData,
  DprStatusTrackerGridDataBaseProperties,
} from '@dpr-details/models/grid';
import {
  DprStatusTrackerAccumulation,
  DprStatusTrackerFormulaContext,
} from '@dpr-details/models';
import {
  DprDetails,
  DprStatusTracker,
  DprStatusTrackerDetails,
} from '@shared/models/dpr';
import { StatusTrackerWithData } from '@shared/models/project-setup';
import { GridSync } from '@grid/models';
import { SyncService } from '@shared/services/grid';
import { Sync } from '@shared/models/grid';
import { StaticMessage } from '@shared/enums';
import { getStaticMessage } from '@shared/utils';
import {
  getStatusTrackerCellId,
  hasNewDivisionByZeroError,
  isStatusTrackerColumnDerived,
  openValidationErrorDialog,
  withoutDerivedStatusTrackerCells,
} from '@dpr-details/utils';
import { DprFormulaCalculationService } from './formula';

@Injectable()
export class DprStatusTrackerGridAdapterService {
  constructor(
    private dialog: MatDialog,
    private syncService: SyncService,
    private dprFormulaCalculationService: DprFormulaCalculationService
  ) {}

  /**
   * A row queued by an earlier offline save holds only the cells that changed, so
   * each edited row is laid over its stored row before its formulas are worked out.
   */
  validateFormulaChanges(
    changes: GridSync<DprStatusTrackerGridData>,
    storedRows: DprStatusTrackerGridData[],
    formulaContext: DprStatusTrackerFormulaContext | undefined,
    statusTracker: StatusTrackerWithData
  ): boolean {
    if (!formulaContext) {
      return true;
    }

    const editedRows = [
      ...(changes?.addedList ?? []),
      ...(changes?.updatedList ?? []),
    ].map((row) => ({
      ...storedRows.find((storedRow) => storedRow.rowId === row.rowId),
      ...row,
    }));

    if (
      !hasNewDivisionByZeroError(
        this.dprFormulaCalculationService.calculateStatusTrackerRows(
          formulaContext,
          editedRows
        ),
        this.dprFormulaCalculationService.calculateStatusTrackerRows(
          formulaContext,
          storedRows
        )
      )
    ) {
      return true;
    }

    openValidationErrorDialog(
      this.dialog,
      statusTracker?.name,
      getStaticMessage(StaticMessage.DivisionByZero)
    );

    return false;
  }

  buildDprStatusTrackerGridData(
    statusTrackers: StatusTrackerWithData[],
    dprDetails: DprDetails,
    accumulations?: Map<number, DprStatusTrackerAccumulation>,
    formulaContexts?: Map<number, DprStatusTrackerFormulaContext>,
    storedColumnIdsByStatusTrackerId?: Map<number, Set<number>>
  ): Map<number, DprStatusTrackerGridData[]> {
    const gridsData = new Map<number, DprStatusTrackerGridData[]>();
    const storedCells = withoutDerivedStatusTrackerCells(
      dprDetails.dprStatusTracker?.dprStatusTrackerDetails,
      statusTrackers,
      storedColumnIdsByStatusTrackerId
    );

    for (const statusTracker of statusTrackers) {
      const columns = statusTracker.columns;
      const accumulation = accumulations?.get(+statusTracker.id);

      const dprStatusTrackersGridData: DprStatusTrackerGridData[] = [];
      // loop each existing row
      for (const row of statusTracker.rows) {
        // get the data specific for that row
        const dprDataForRow = storedCells.filter((x) => x.rowId === row.id);
        const entryId = `${dprDetails.dprStatusTracker.dprDetailId}_${statusTracker.id}_${row.id}`;
        // fill the base row data (id, rowId, row name, group name)
        const dprGridDataRow = {
          id: entryId,
          rowId: +row.id,
          row: row.name,
          groupId: +row.rowGroupId,
          cellColors: {},
        };

        // for each column, fill the dynamic grid data. Whatever a formula column
        // arrives with is replaced below, once the carry-overs it can read are in.
        for (const column of columns) {
          const data = dprDataForRow.find((x) => x.columnId === column.id);
          const carryOverValues = accumulation?.valuesByColumnId.get(
            +column.id
          );

          if (carryOverValues) {
            // An accumulated column is read from the previous DPR rather than from
            // what is stored, which only reflects the previous DPR as it stood the
            // last time this cell was written. A blank carried value stays blank.
            dprGridDataRow[column.name] = carryOverValues.get(+row.id) ?? null;
          } else {
            dprGridDataRow[column.name] = data?.value ?? null;
          }

          if (data?.colorTypeId) {
            dprGridDataRow.cellColors = {
              ...dprGridDataRow.cellColors,
              [column.name]: data.colorTypeId,
            };
          }
        }

        // add data to the array
        dprStatusTrackersGridData.push(dprGridDataRow);
      }

      const formulaContext = formulaContexts?.get(+statusTracker.id);

      if (formulaContext) {
        this.dprFormulaCalculationService.applyStatusTrackerFormulas(
          formulaContext,
          dprStatusTrackersGridData
        );
      }

      // map the data to the specific status tracker id
      gridsData.set(+statusTracker.id, dprStatusTrackersGridData);
    }

    return gridsData;
  }

  convertDprStatusTrackerDetailsSyncToStatusTrackerGridSync(
    data: GridSync<DprStatusTrackerDetails>,
    statusTracker: StatusTrackerWithData,
    gridsData: DprStatusTrackerGridData[]
  ): GridSync<DprStatusTrackerGridData> {
    const result =
      this.syncService.getDefaultSyncModel<DprStatusTrackerGridData>();

    for (const row of statusTracker.rows) {
      const rowData = data.addedList
        .filter((x) => x.rowId === row.id)
        .concat(data.updatedList.filter((x) => x.rowId === row.id));

      if (!rowData?.length) {
        continue;
      }
      const entryId = gridsData.find((x) => x.rowId === row.id).id ?? uuid();

      const dprGridDataRow = {
        id: entryId,
        rowId: +row.id,
        row: row.name,
        groupId: +row.rowGroupId,
        cellColors: rowData.reduce(
          (acc, curr) => ({
            ...acc,
            [statusTracker.columns.find((x) => x.id === curr.columnId).name]:
              curr.colorTypeId,
          }),
          {}
        ),
      };

      for (const entryData of rowData) {
        const columnName = statusTracker.columns.find(
          (x) => x.id === entryData.columnId
        ).name;
        dprGridDataRow[columnName] = entryData.value;
      }

      result.updatedList.push(dprGridDataRow);
    }

    result.deletedIds = [...data.deletedIds];

    return result;
  }

  buildSyncDataFromGridData(
    gridSyncData: GridSync<DprStatusTrackerGridData>,
    prevgridSyncData: GridSync<DprStatusTrackerDetails>,
    statusTracker: StatusTrackerWithData,
    dprStatusTracker: DprStatusTracker
  ): Sync<DprStatusTrackerDetails> {
    // new empty sync model
    const syncData =
      this.syncService.getDefaultSyncModel<DprStatusTrackerDetails>();

    // get all the data
    const updatedData = [
      ...gridSyncData.updatedList,
      ...gridSyncData.addedList,
    ];

    // this is data that has been previously updated during offline saves
    // we need to check bellow if the property has been previously updated on
    // a previous save to be sure we also apply those changes when remapping the data
    // to the sync model (otherwise changes from previous iterations will be lost during mapping)
    const prevUpdatedData = [
      ...prevgridSyncData.updatedList,
      ...prevgridSyncData.addedList,
    ];

    // A derived cell is never sent, even when the earlier offline changes include
    // it: its value on the row is what the grid worked out, not what the user entered.
    const derivedColumnNames = new Set(
      statusTracker.columns
        .filter((column) => isStatusTrackerColumnDerived(statusTracker, column))
        .map((column) => column.name)
    );

    for (const data of updatedData) {
      const processedCellColumnChanges: string[] = [];

      // loop through each prop of each entry
      for (const prop in data) {
        // check if the property is not in the standard list,
        // meaning it is a dynamic property representing a cell value
        if (
          DprStatusTrackerGridDataBaseProperties.includes(prop) ||
          derivedColumnNames.has(prop)
        ) {
          continue;
        }

        const columnId = statusTracker.columns.find((y) => y.name === prop).id;

        // make sure the property was updated to avoid unnecessary updates
        // otherwise all columns on same row would be updated if one was updated
        if (
          (!gridSyncData.changedProperties ||
            !gridSyncData.changedProperties[`${data.id}__${prop}`]) &&
          !prevUpdatedData.find(
            (x) =>
              x.columnId === columnId &&
              x.rowId === data.rowId &&
              x.dprStatusTrackerId === dprStatusTracker.id
          )
        ) {
          continue;
        }

        const existingDprStatusTrackerDetail =
          dprStatusTracker.dprStatusTrackerDetails.find(
            (y) =>
              y.rowId === data.rowId &&
              y.columnId === columnId &&
              y.id != null &&
              !isNaN(+y.id) &&
              y.statusTrackerId === statusTracker.id
          );

        if (
          (data[prop] == null ||
            String.isNullOrWhiteSpace(data[prop].toString())) &&
          data.cellColors[prop] == null
        ) {
          if (
            existingDprStatusTrackerDetail &&
            (existingDprStatusTrackerDetail.value !== null ||
              existingDprStatusTrackerDetail.colorTypeId !== null)
          ) {
            syncData.deletedIds.push(existingDprStatusTrackerDetail.id);
          }

          continue;
        }

        let newValue: any = {
          value: data[prop],
          statusTrackerId: statusTracker.id,
          rowId: data.rowId,
          columnId,
          dprStatusTrackerId: dprStatusTracker.id,
          id:
            existingDprStatusTrackerDetail?.id ??
            getStatusTrackerCellId(
              dprStatusTracker.dprDetailId,
              statusTracker.id,
              data.rowId,
              columnId
            ),
        };

        if (data.cellColors[prop]) {
          newValue = {
            ...newValue,
            colorTypeId: data.cellColors[prop],
          };

          processedCellColumnChanges.push(prop);
        }

        // if the data is present in the initial list, add it to the updated list
        // otherwise, add it to the added list
        if (
          existingDprStatusTrackerDetail?.id != null &&
          !isNaN(+existingDprStatusTrackerDetail?.id)
        ) {
          syncData.updatedList.push(newValue);
        } else {
          syncData.addedList.push(newValue);
        }
      }

      // apply color changes on the cells that had only the color changed
      for (const prop in data.cellColors) {
        if (
          !processedCellColumnChanges.find((x) => x === prop) &&
          !derivedColumnNames.has(prop)
        ) {
          const columnId = statusTracker.columns.find(
            (y) => y.name === prop
          ).id;

          // make sure the property was updated to avoid unnecessary adds
          // otherwise new entries for all columns that have colors will be added
          if (
            !data.cellColors[prop] ||
            !gridSyncData.changedProperties ||
            (!gridSyncData.changedProperties[`${data.id}__${prop}`] &&
              !prevUpdatedData.find(
                (x) =>
                  x.columnId === columnId &&
                  x.rowId === data.rowId &&
                  x.dprStatusTrackerId === dprStatusTracker.id
              ))
          ) {
            continue;
          }

          const newValue: any = {
            value: data[prop],
            statusTrackerId: statusTracker.id,
            rowId: data.rowId,
            columnId,
            colorTypeId: data.cellColors[prop],
            dprStatusTrackerId: dprStatusTracker.id,
            id: getStatusTrackerCellId(
              dprStatusTracker.dprDetailId,
              statusTracker.id,
              data.rowId,
              columnId
            ),
          };

          syncData.addedList.push(newValue);
        }
      }
    }

    syncData.deletedIds = [...gridSyncData.deletedIds, ...syncData.deletedIds];

    return syncData;
  }
}
